import { apiClient } from '$lib/api/client';
import type { Filters } from '$lib/types/api';
import { handleError, ValidationError } from '$lib/utils/error-handling';
import { notifications } from '$lib/stores/notifications';
import { neighborSamples } from '$lib/stores/neighbors';
import { selectedIndustryCentroids } from '$lib/stores/data';
import {
  queryResult,
  queryRunning,
  resultStats,
  queryFilters,
  scoreFilterRange
} from '$lib/stores/selection';
import { get } from 'svelte/store';
import type { Company } from '$lib/types';
import { turnstile } from '$lib/turnstile';
import { setOriginalResults } from '$lib/stores/mixedResults';
import { pixel } from '$lib';

interface NeighborRequest {
  sample_companies: string[];
  filters?: Filters;
  data_pool_ids?: string[];
  contact_mode?: 'single' | 'multiple';
  turnstile_token?: string;
}

interface Contact {
  id: string;
  company_id: string;
  name: string;
  role: string;
}

interface NeighborResponse {
  companies: Company[];
  contacts: Contact[];
  stats: Record<string, unknown>;
}

interface CompanyWithContacts extends Company {
  contacts: Contact[];
}

// Helper function to assemble companies with their contacts
function assembleCompaniesWithContacts(response: NeighborResponse): CompanyWithContacts[] {
  const { companies, contacts } = response;

  // Ensure contacts is always an array (handle null, undefined, or missing)
  const contactsArray = contacts || [];

  // Group contacts by company ID
  const contactsByCompany = contactsArray.reduce(
    (acc, contact) => {
      if (!acc[contact.company_id]) {
        acc[contact.company_id] = [];
      }
      acc[contact.company_id].push(contact);
      return acc;
    },
    {} as Record<string, Contact[]>
  );

  // Attach contacts to companies
  return companies.map((company) => ({
    ...company,
    contacts: contactsByCompany[company.id] || []
  }));
}

export async function submitNeighborSamples(): Promise<void> {
  try {
    queryRunning.set(true);
    queryResult.set([]);
    resultStats.set({});
    scoreFilterRange.set([0, 100]); // Reset score filter to full range

    // Also reset mixed results to ensure no data is displayed while loading
    setOriginalResults([]);

    const samples = get(neighborSamples);
    const filters = get(queryFilters);
    const centroids = Array.from(get(selectedIndustryCentroids).keys());
    const ids = samples.map((s) => s.id);

    // Validate minimum samples
    if (ids.length < 1) {
      throw new ValidationError(
        'samples',
        'At least one company must be selected for neighbor analysis',
        { currentCount: ids.length }
      );
    }

    // Validate maximum samples (reasonable limit)
    if (ids.length > 10) {
      throw new ValidationError(
        'samples',
        'Too many samples selected. Maximum 10 companies allowed',
        { currentCount: ids.length }
      );
    }

    // Get Turnstile token
    let turnstileToken: string | undefined;
    try {
      turnstileToken = await turnstile.getToken();
    } catch {
      // Continue without token if it fails
    }

    const request: NeighborRequest = {
      sample_companies: ids,
      filters,
      data_pool_ids: centroids,
      contact_mode: filters.contact_mode || 'single',
      turnstile_token: turnstileToken
    };

    const response = await apiClient.post<NeighborResponse>('/deep-iq/centroid', request);

    if (response.data.companies && Array.isArray(response.data.companies)) {
      // Assemble companies with their contacts
      const companiesWithContacts = assembleCompaniesWithContacts(response.data);
      queryResult.set(companiesWithContacts);
      resultStats.set(response.data.stats || {});

      const companyCount = response.data.companies.length;
      const contactCount = response.data.contacts?.length || 0;
      notifications.success(
        `Found ${companyCount} similar ${companyCount === 1 ? 'company' : 'companies'} with ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'} based on your samples and filters`
      );

      // Track first search conversion with Meta Pixel
      if (typeof window !== 'undefined' && !sessionStorage.getItem('first_search_tracked')) {
        pixel.trackConversion('Search', {
          search_type: 'neighbor',
          company_count: companyCount,
          contact_count: contactCount,
          sample_count: ids.length,
          sample_company_ids: JSON.stringify(ids),
          // Filter details
          has_postal_codes: !!(filters.postal_codes && filters.postal_codes.length > 0),
          postal_codes_count: filters.postal_codes?.length || 0,
          has_cantons: !!(filters.cantons && filters.cantons.length > 0),
          cantons_count: filters.cantons?.length || 0,
          has_company_size: !!(
            filters.company_size_range && filters.company_size_range.some((v) => v !== null)
          ),
          has_contact_roles: !!(filters.contact_roles && filters.contact_roles.length > 0),
          contact_roles_count: filters.contact_roles?.length || 0,
          has_founding_year: !!(
            filters.founding_year_range && filters.founding_year_range.some((v) => v !== null)
          ),
          has_ai_search: !!filters.embedding_query,
          industry_segments: centroids.length,
          industry_segment_ids: JSON.stringify(centroids)
        });
        sessionStorage.setItem('first_search_tracked', 'true');
      }
    } else {
      queryResult.set([]);
      resultStats.set({});
      notifications.info('No similar companies found for the selected samples');
    }
  } catch (error) {
    const errorMessage = await handleError(error, {
      operation: 'submitNeighborSamples',
      component: 'neighbors-api',
      metadata: { sampleCount: get(neighborSamples).length }
    });

    notifications.error(errorMessage);

    // Reset state on error
    queryResult.set([]);
    resultStats.set({});
  } finally {
    queryRunning.set(false);
  }
}

export async function searchCompanySuggestions(
  query: string,
  limit: number = 50
): Promise<Company[]> {
  try {
    if (!query || query.length < 2) {
      return [];
    }

    const response = await apiClient.get<{ companies: Company[] }>('/deep-iq/search/companies', {
      query,
      limit
    });

    return response.data.companies || [];
  } catch (error) {
    await handleError(error, {
      operation: 'searchCompanySuggestions',
      component: 'neighbors-api',
      metadata: { query: query.substring(0, 50) }
    });

    return [];
  }
}
